import PropTypes from "prop-types";
import { memo, useCallback } from "react";
import SmartDPMarketingHeadline from "app/pages/SmartDP/SmartDPMarketingHeadline/SmartDPMarketingHeadline";
import SmartDPHeroCommercialTag from "app/pages/SmartDP/SmartDPHeroCommercialTag/SmartDPHeroCommercialTag";
import { FormattedMessage } from "react-intl";
import { isAbsoluteUrl } from "app/utils/urlDataExtractor";
import { useNavigate } from "react-router-dom";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { Button } from "app/components/ui/Button/Button";
import {
	buildHotelOnlySearchParamsForDestination,
	buildHotelOnlySearchTagPayload,
} from "app/utils/smartDPUtils";
import { ArrowRightIcon } from "app/icons/navigation";

// Single place for hero CTA navigation + analytics. Used by this component's onClick handlers and by
// SmartDPHeroCommercialCarousel's native click delegation for Swiper loop duplicate slides.
export function executeHeroCommercialCta({
	heroCommercialMerch = {},
	navigate,
	track,
	destinations,
}) {
	track("click_hero");
	const shouldNavigateToListing =
		heroCommercialMerch?.displayOptions?.enableRedirectionToHotelOnlyListingPage &&
		heroCommercialMerch?.destinationFilters?.length > 0;

	if (shouldNavigateToListing) {
		const destinationResort = destinations?.find(
			destination =>
				destination.code === Number(heroCommercialMerch?.destinationFilters[0]?.id)
		);

		if (destinationResort) {
			const merchRedirectUrlData = {
				pathname: "/hotelonly/listing",
				search: `?${buildHotelOnlySearchParamsForDestination(destinationResort?.code)}`,
			};
			track("sdp_search_submit", buildHotelOnlySearchTagPayload(destinationResort?.code));
			navigate(merchRedirectUrlData);
		}
	} else {
		const redirectionUrl =
			heroCommercialMerch?.displayOptions?.heroCommercial?.url || undefined;
		const merchandisingCode = heroCommercialMerch?.code;
		if (redirectionUrl) {
			if (isAbsoluteUrl(redirectionUrl)) {
				window.location = redirectionUrl;
			} else {
				navigate({ pathname: `/${redirectionUrl}` });
			}
		} else {
			navigate({
				pathname: `/merch/${merchandisingCode}`,
			});
		}
	}
}

const SmartDPHeroCommercialContent = ({ heroCommercialMerch = {}, destinations = [] }) => {
	const { track } = useAnalytics();
	const navigate = useNavigate();
	const title =
		heroCommercialMerch?.displayOptions?.heroCommercial?.merchandisingTitle ||
		heroCommercialMerch?.displayOptions?.smartDP?.merchandisingTitle;
	const subtitle =
		heroCommercialMerch?.displayOptions?.heroCommercial?.merchandisingSubtitle ||
		heroCommercialMerch?.displayOptions?.smartDP?.merchandisingSubtitle;
	const showCta = heroCommercialMerch?.displayOptions?.heroCommercial?.showCta;
	const labelCta = heroCommercialMerch?.displayOptions?.heroCommercial?.labelCta || (
		<FormattedMessage id="landingPage.footer.redirect.cta.text" />
	);
	const heroTextAlign =
		heroCommercialMerch?.displayOptions?.heroCommercial?.heroTextAlign ||
		heroCommercialMerch?.displayOptions?.heroTextAlign ||
		"center";
	const tag = heroCommercialMerch?.displayOptions?.heroCommercial?.tag || undefined;
	const enableCopyTag = heroCommercialMerch?.displayOptions?.heroCommercial?.copyToClipboard;
	const handleCtaClicked = useCallback(() => {
		executeHeroCommercialCta({ heroCommercialMerch, navigate, track, destinations });
	}, [heroCommercialMerch, navigate, track]);

	const lowestPrice = heroCommercialMerch?.lowestPrice;
	const homePageLogo = heroCommercialMerch?.displayOptions?.heroCommercial?.logoHomepage;
	return (
		<div
			className="smartDP-hero-commercial__image-headline"
			data-testid="smartDP-hero-commercial-image-headline"
		>
			{tag && (
				<SmartDPHeroCommercialTag tag={tag?.toUpperCase()} enableCopy={enableCopyTag} />
			)}

			{homePageLogo && (
				<img
					src={homePageLogo}
					alt=""
					className="smartDP-hero-commercial__logo-homepage"
					onClick={handleCtaClicked}
				/>
			)}

			<SmartDPMarketingHeadline
				title={title}
				subtitle={subtitle}
				textAlign={heroTextAlign}
				lowestPrice={lowestPrice}
				hideSubtitle={Boolean(homePageLogo)}
				{...(showCta && { onClick: handleCtaClicked })}
			/>

			{showCta && (
				<Button
					variant="secondary"
					endIcon={<ArrowRightIcon />}
					className="smartDP-hero-commercial__redirect--button"
					data-testid="smartDP-hero-commercial-redirect-button"
					onClick={handleCtaClicked}
				>
					{labelCta}
				</Button>
			)}
		</div>
	);
};
SmartDPHeroCommercialContent.propTypes = {
	heroCommercialMerch: PropTypes.object,
	destinations: PropTypes.array,
};
export default memo(SmartDPHeroCommercialContent);
